import { ProCard, ProDescriptions } from '@ant-design/pro-components';
import { history, useAccess } from '@umijs/max';
import {
  Button,
  Form,
  Modal,
  message,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
} from 'antd';
import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CrmList } from '@/components/CrmList';
import Empty from '@/components/QuietEmpty';
import { BusinessModal } from '@/modules/zy-crm/components/business-modal';
import { DetailNavigation } from '@/modules/zy-crm/components/detail-navigation';
import { DetailSurface } from '@/modules/zy-crm/components/detail-surface';
import { LeadEditModal } from '@/modules/zy-crm/components/lead-edit-modal';
import { MarketingCampaignEditModal } from '@/modules/zy-crm/components/marketing-campaign-edit-modal';
import { MarketingCampaignStatsSection } from '@/modules/zy-crm/components/marketing-campaign-stats-section';
import {
  formatCrmDate,
  resolveRelationText,
} from '@/modules/zy-crm/utils/display';
import { PERMISSION_ACTION_CODES } from '@/permissions/definitions';
import { hasActionAccess } from '@/permissions/helpers';
import { queryLeads } from '@/services/bff/lead.api';
import {
  addMarketingCampaignMember,
  queryMarketingCampaignDetail,
} from '@/services/bff/marketing-campaign.api';
import type {
  MarketingCampaignDetail,
  MarketingCampaignMemberItem,
  MarketingCampaignTouchpointItem,
} from '@/services/bff/types/marketing-campaign';
import styles from './style.module.less';

const { Paragraph, Text, Title } = Typography;

export type MarketingCampaignDrawerSectionKey =
  | 'overview'
  | 'target'
  | 'result'
  | 'touchpoints'
  | 'members'
  | 'stats';

type MarketingCampaignDrawerOpenInput = {
  campaignUuid?: string;
  campaignName?: ReactNode;
  initialSection?: MarketingCampaignDrawerSectionKey;
};

type MarketingCampaignDetailDrawerContextValue = {
  openMarketingCampaign: (input: MarketingCampaignDrawerOpenInput) => void;
  hasProvider: boolean;
};

type MarketingCampaignReferenceLinkProps = {
  campaignUuid?: string;
  campaignName?: ReactNode;
  fallbackName?: ReactNode;
  emptyText?: ReactNode;
};

type MetaItemProps = {
  label: string;
  value: ReactNode;
};

type SummaryItem = {
  label: string;
  value: ReactNode;
};

type SectionPanelProps = {
  title: string;
  children: ReactNode;
  extra?: ReactNode;
};

type MembersSectionActions = {
  canManage: boolean;
  onAdd: () => void;
  onCreate: () => void;
};

const SECTION_ITEMS: Array<{
  key: MarketingCampaignDrawerSectionKey;
  label: string;
}> = [
  { key: 'overview', label: '活动概览' },
  { key: 'target', label: '目标与预算' },
  { key: 'result', label: '转化结果' },
  { key: 'touchpoints', label: '接入点' },
  { key: 'members', label: '关联线索' },
  { key: 'stats', label: '效果分析' },
];

const SECTION_KEY_SET = new Set<MarketingCampaignDrawerSectionKey>(
  SECTION_ITEMS.map((item) => item.key),
);

const ATTRIBUTION_TEXT_MAP: Record<string, string> = {
  first_touch: '首次触达',
  last_touch: '最后触达',
  firstTouch: '首次触达',
  lastTouch: '最后触达',
  manual: '手工录入',
  form_submit: '表单提交',
  formSubmit: '表单提交',
  import: '导入',
};

const MarketingCampaignDetailDrawerContext =
  createContext<MarketingCampaignDetailDrawerContextValue | null>(null);

const formatMoney = (value?: number) => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) {
    return '-';
  }
  return Number(value).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const STATUS_MAP: Record<string, { text: string; color: string }> = {
  draft: { text: '草稿', color: 'default' },
  pending: { text: '筹备中', color: 'processing' },
  running: { text: '进行中', color: 'success' },
  paused: { text: '已暂停', color: 'warning' },
  closed: { text: '已关闭', color: 'default' },
  finished: { text: '已结束', color: 'blue' },
};

const normalizeText = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const formatAttributionText = (value?: string) => {
  const normalized = normalizeText(value);
  if (!normalized) {
    return '-';
  }
  if (ATTRIBUTION_TEXT_MAP[normalized]) {
    return ATTRIBUTION_TEXT_MAP[normalized];
  }
  if (/[\u4e00-\u9fa5]/.test(normalized)) {
    return normalized;
  }
  return '-';
};

export const normalizeMarketingCampaignDrawerSection = (
  value?: string | null,
): MarketingCampaignDrawerSectionKey =>
  value && SECTION_KEY_SET.has(value as MarketingCampaignDrawerSectionKey)
    ? (value as MarketingCampaignDrawerSectionKey)
    : 'overview';

export const buildMarketingCampaignListPath = ({
  campaignUuid,
  section,
}: {
  campaignUuid?: string;
  section?: MarketingCampaignDrawerSectionKey;
}) => {
  const params = new URLSearchParams();
  const uuid = normalizeText(campaignUuid);
  if (uuid) {
    params.set('campaignUuid', uuid);
  }
  if (section && section !== 'overview') {
    params.set('section', section);
  }
  const query = params.toString();
  return query ? `/marketing/campaign?${query}` : '/marketing/campaign';
};

const renderStatus = (value?: string) => {
  if (!value) {
    return '-';
  }
  const config = STATUS_MAP[value] || {
    text: value,
    color: 'default',
  };
  return <Tag color={config.color}>{config.text}</Tag>;
};

function resolveOwnerValue(detail?: MarketingCampaignDetail) {
  return detail?.ownerUuid || detail?.ownerUserUuid;
}

function resolveSourceChannelValue(detail?: MarketingCampaignDetail) {
  return detail?.sourceChannelUuid || detail?.sourceChannel;
}

function resolveOwnerText(detail?: MarketingCampaignDetail) {
  return resolveRelationText({
    value: resolveOwnerValue(detail),
    directName: detail?.ownerName,
  });
}

function resolveSourceChannelText(detail?: MarketingCampaignDetail) {
  return resolveRelationText({
    value: resolveSourceChannelValue(detail),
    directName: detail?.sourceChannelName,
  });
}

function buildPeriodText(detail?: MarketingCampaignDetail) {
  const start = formatCrmDate(detail?.startTime, 'date');
  const end = formatCrmDate(detail?.endTime, 'date');
  if (start === '-' && end === '-') {
    return '-';
  }
  return `${start} 至 ${end}`;
}

function MetaItem({ label, value }: MetaItemProps) {
  return (
    <div className={styles.metaItem}>
      <span>{label}</span>
      <strong>{value || '-'}</strong>
    </div>
  );
}

function SummaryList({ items }: { items: SummaryItem[] }) {
  return (
    <CrmList
      dataSource={items}
      renderItem={(item) => (
        <CrmList.Item>
          <Text>{item.label}</Text>
          <Text strong>{item.value}</Text>
        </CrmList.Item>
      )}
    />
  );
}

function SectionPanel({ title, children, extra }: SectionPanelProps) {
  return (
    <ProCard
      title={title}
      extra={extra}
      className={styles.sectionCard}
      styles={{ body: { padding: 12 } }}
    >
      {children}
    </ProCard>
  );
}

const renderTouchpoints = (
  campaignUuid?: string,
  items: MarketingCampaignTouchpointItem[] = [],
) => {
  if (!items.length) {
    return <Empty description={false} />;
  }
  return (
    <CrmList
      dataSource={items}
      renderItem={(item) => (
        <CrmList.Item
          actions={
            campaignUuid
              ? [
                  <Button
                    key="members"
                    type="link"
                    onClick={() => {
                      const params = new URLSearchParams();
                      if (item.uuid) {
                        params.set('campaignTouchpointUuid', item.uuid);
                      }
                      const query = params.toString();
                      history.push(
                        query
                          ? `/marketing/campaign/${campaignUuid}/member?${query}`
                          : `/marketing/campaign/${campaignUuid}/member`,
                      );
                    }}
                  >
                    查看成员
                  </Button>,
                ]
              : undefined
          }
        >
          <CrmList.Item.Meta
            title={item.touchpointName || item.touchpointCode || item.uuid}
            description={[
              item.touchpointType ? `类型 ${item.touchpointType}` : null,
              item.touchpointCode ? `编码 ${item.touchpointCode}` : null,
              item.landingPageName ? `落地页 ${item.landingPageName}` : null,
              item.formCode ? `表单 ${item.formCode}` : null,
              item.externalPlatform ? `平台 ${item.externalPlatform}` : null,
            ]
              .filter(Boolean)
              .join('，')}
          />
          <div>
            {item.enabled === false ? (
              <Tag color="default">停用</Tag>
            ) : (
              <Tag color="success">启用</Tag>
            )}
          </div>
        </CrmList.Item>
      )}
    />
  );
};

const renderMembers = (
  campaignUuid?: string,
  items: MarketingCampaignMemberItem[] = [],
  actions?: { canManage: boolean; onAdd?: () => void; onCreate?: () => void },
) => {
  if (!items.length) {
    return actions?.canManage ? (
      <Empty description="还没有关联的线索，可添加已有线索或新建线索">
        <Space>
          <Button onClick={actions.onAdd}>添加关联</Button>
          <Button type="primary" onClick={actions.onCreate}>
            新建线索
          </Button>
        </Space>
      </Empty>
    ) : (
      <Empty description={false} />
    );
  }
  return (
    <>
      <CrmList
        dataSource={items}
        renderItem={(item) => (
          <CrmList.Item>
            <CrmList.Item.Meta
              title={
                item.leadName ||
                item.customerName ||
                item.contactName ||
                item.uuid
              }
              description={[
                item.attributionType
                  ? `归因 ${formatAttributionText(item.attributionType)}`
                  : null,
                item.submitTime
                  ? `提交时间 ${formatCrmDate(item.submitTime)}`
                  : null,
                item.ownerNameSnapshot
                  ? `负责人 ${item.ownerNameSnapshot}`
                  : null,
                item.scoreSnapshot !== undefined
                  ? `评分 ${item.scoreSnapshot}`
                  : null,
              ]
                .filter(Boolean)
                .join('，')}
            />
            {item.isDuplicate ? (
              <Tag color="warning">重复</Tag>
            ) : (
              <Tag color="success">有效</Tag>
            )}
          </CrmList.Item>
        )}
      />
      {campaignUuid ? (
        <div className={styles.sectionActionRow}>
          <Button
            onClick={() =>
              history.push(`/marketing/campaign/${campaignUuid}/member`)
            }
          >
            查看全部
          </Button>
        </div>
      ) : null}
    </>
  );
};

function renderSection(
  activeSection: MarketingCampaignDrawerSectionKey,
  detail?: MarketingCampaignDetail,
  memberActions?: MembersSectionActions,
) {
  if (!detail) {
    return null;
  }

  if (activeSection === 'overview') {
    return (
      <div className={styles.panelStack} data-crm-stack>
        <SectionPanel title="活动概览">
          <ProDescriptions<MarketingCampaignDetail>
            dataSource={detail}
            column={2}
          >
            <ProDescriptions.Item label="活动名称" dataIndex="name" />
            <ProDescriptions.Item label="活动编号" dataIndex="code" />
            <ProDescriptions.Item label="活动类型" dataIndex="type" />
            <ProDescriptions.Item label="活动状态">
              {renderStatus(detail.status)}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="负责人">
              {resolveOwnerText(detail)}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="主来源渠道">
              {resolveSourceChannelText(detail)}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="活动说明" span={2}>
              <Paragraph className={styles.recordDescription}>
                {detail.description || '-'}
              </Paragraph>
            </ProDescriptions.Item>
            <ProDescriptions.Item label="备注" span={2}>
              <Paragraph className={styles.recordDescription}>
                {detail.remark || '-'}
              </Paragraph>
            </ProDescriptions.Item>
          </ProDescriptions>
        </SectionPanel>
      </div>
    );
  }

  if (activeSection === 'target') {
    return (
      <div className={styles.panelStack} data-crm-stack>
        <SectionPanel title="目标与预算">
          <ProDescriptions<MarketingCampaignDetail>
            dataSource={detail}
            column={2}
          >
            <ProDescriptions.Item label="开始时间">
              {formatCrmDate(detail.startTime)}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="结束时间">
              {formatCrmDate(detail.endTime)}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="预算">
              {formatMoney(detail.budgetAmount)}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="目标线索数">
              {detail.goalLeadCount ?? '-'}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="目标商机金额">
              {formatMoney(detail.goalOpportunityAmount)}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="活动周期">
              {buildPeriodText(detail)}
            </ProDescriptions.Item>
          </ProDescriptions>
        </SectionPanel>
      </div>
    );
  }

  if (activeSection === 'result') {
    return (
      <div className={styles.panelStack} data-crm-stack>
        <SectionPanel title="转化结果">
          <SummaryList
            items={[
              { label: '累计线索数', value: detail.leadCount ?? 0 },
              { label: '有效线索数', value: detail.validLeadCount ?? 0 },
              { label: '重复线索数', value: detail.duplicateLeadCount ?? 0 },
              {
                label: '转客户数',
                value: detail.transformedCustomerCount ?? 0,
              },
              {
                label: '转商机数',
                value: detail.transformedOpportunityCount ?? 0,
              },
              {
                label: '赢单金额',
                value: formatMoney(detail.wonOpportunityAmount),
              },
            ]}
          />
        </SectionPanel>
      </div>
    );
  }

  if (activeSection === 'touchpoints') {
    return (
      <div className={styles.panelStack} data-crm-stack>
        <SectionPanel title="接入点">
          {renderTouchpoints(detail.uuid, detail.touchpoints)}
        </SectionPanel>
      </div>
    );
  }

  if (activeSection === 'members') {
    return (
      <div className={styles.panelStack} data-crm-stack>
        <SectionPanel
          title="关联线索"
          extra={
            memberActions?.canManage ? (
              <Space>
                <Button size="small" onClick={memberActions.onAdd}>
                  添加关联
                </Button>
                <Button
                  size="small"
                  type="primary"
                  onClick={memberActions.onCreate}
                >
                  新建线索
                </Button>
              </Space>
            ) : undefined
          }
        >
          {renderMembers(detail.uuid, detail.members, {
            canManage: Boolean(memberActions?.canManage),
            onAdd: memberActions?.onAdd,
            onCreate: memberActions?.onCreate,
          })}
        </SectionPanel>
      </div>
    );
  }

  if (activeSection === 'stats') {
    return (
      <div className={styles.panelStack} data-crm-stack>
        <SectionPanel
          title="效果分析"
          extra={
            <Button
              size="small"
              onClick={() =>
                history.push(`/marketing/campaign/${detail.uuid}/analysis`)
              }
            >
              查看全部分析
            </Button>
          }
        >
          <MarketingCampaignStatsSection detail={detail} />
        </SectionPanel>
      </div>
    );
  }

  return null;
}

export function useMarketingCampaignDetailDrawer() {
  const ctx = useContext(MarketingCampaignDetailDrawerContext);
  return (
    ctx || {
      hasProvider: false,
      openMarketingCampaign: ({
        campaignUuid,
      }: MarketingCampaignDrawerOpenInput) => {
        if (campaignUuid) {
          history.push(`/marketing/campaign/${campaignUuid}`);
        }
      },
    }
  );
}

export function MarketingCampaignDetailDrawerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [campaignUuid, setCampaignUuid] = useState<string>();
  const [fallbackName, setFallbackName] = useState<ReactNode>();
  const [initialSection, setInitialSection] =
    useState<MarketingCampaignDrawerSectionKey>('overview');

  const openMarketingCampaign = useCallback(
    (input: MarketingCampaignDrawerOpenInput) => {
      const uuid = normalizeText(input.campaignUuid);
      if (!uuid) {
        message.warning('该记录缺少市场活动 UUID，无法打开详情');
        return;
      }
      setCampaignUuid(uuid);
      setFallbackName(input.campaignName);
      setInitialSection(
        normalizeMarketingCampaignDrawerSection(input.initialSection),
      );
      setOpen(true);
    },
    [],
  );

  const value = useMemo(
    () => ({ openMarketingCampaign, hasProvider: true }),
    [openMarketingCampaign],
  );

  return (
    <MarketingCampaignDetailDrawerContext.Provider value={value}>
      {children}
      <MarketingCampaignDetailDrawer
        open={open}
        campaignUuid={campaignUuid}
        fallbackName={fallbackName}
        initialSection={initialSection}
        onClose={() => setOpen(false)}
      />
    </MarketingCampaignDetailDrawerContext.Provider>
  );
}

export function MarketingCampaignReferenceLink({
  campaignUuid,
  campaignName,
  fallbackName,
  emptyText = '-',
}: MarketingCampaignReferenceLinkProps) {
  const { openMarketingCampaign } = useMarketingCampaignDetailDrawer();
  const label = campaignName || fallbackName || emptyText;

  if (!normalizeText(campaignUuid)) {
    return <Text type="secondary">{label}</Text>;
  }

  return (
    <Button
      type="link"
      className={styles.trigger}
      onClick={() =>
        openMarketingCampaign({
          campaignUuid,
          campaignName: label,
        })
      }
    >
      {label}
    </Button>
  );
}

function AddLeadToCampaignModal({
  open,
  campaignUuid,
  touchpoints = [],
  onCancel,
  onSuccess,
}: {
  open: boolean;
  campaignUuid?: string;
  touchpoints?: MarketingCampaignTouchpointItem[];
  onCancel: () => void;
  onSuccess: () => void;
}) {
  const [form] = Form.useForm();
  const [leadOptions, setLeadOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setLeadOptions([]);
    }
  }, [form, open]);

  useEffect(() => {
    return () => {
      if (searchTimer.current) {
        clearTimeout(searchTimer.current);
      }
    };
  }, []);

  const searchLeads = useCallback((keyword: string) => {
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const resp = await queryLeads({ keyword, page: 1, pageSize: 20 });
        setLeadOptions(
          (resp?.list || [])
            .filter((lead) => lead.uuid)
            .map((lead) => ({
              value: lead.uuid,
              label: lead.mobile
                ? `${lead.name || '未命名线索'}（${lead.mobile}）`
                : lead.name || lead.uuid,
            })),
        );
      } catch {
        setLeadOptions([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, []);

  const handleOk = async () => {
    if (!campaignUuid) {
      return;
    }
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      await addMarketingCampaignMember(campaignUuid, {
        leadUuid: values.leadUuid,
        campaignTouchpointUuid: values.campaignTouchpointUuid || undefined,
      });
      message.success('线索已关联到活动');
      onSuccess();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '关联线索失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="添加关联线索"
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      okText="关联"
      cancelText="取消"
      confirmLoading={submitting}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="leadUuid"
          label="线索"
          rules={[{ required: true, message: '请选择要关联的线索' }]}
        >
          <Select
            showSearch
            filterOption={false}
            onSearch={searchLeads}
            loading={searching}
            options={leadOptions}
            placeholder="输入姓名或手机号搜索已有线索"
            notFoundContent={searching ? '搜索中…' : '暂无匹配线索'}
          />
        </Form.Item>
        <Form.Item name="campaignTouchpointUuid" label="归因接入点（可选）">
          <Select
            allowClear
            placeholder="请选择接入点"
            options={(touchpoints || [])
              .filter((item) => item.uuid)
              .map((item) => ({
                value: item.uuid,
                label: item.touchpointName || item.touchpointCode || item.uuid,
              }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function MarketingCampaignDetailDrawer({
  open,
  campaignUuid,
  fallbackName,
  initialSection,
  onClose,
}: {
  open: boolean;
  campaignUuid?: string;
  fallbackName?: ReactNode;
  initialSection: MarketingCampaignDrawerSectionKey;
  onClose: () => void;
}) {
  const access = useAccess() as Record<string, boolean>;
  const canManageMembers = hasActionAccess(
    access,
    'marketing-campaigns',
    PERMISSION_ACTION_CODES.marketingCampaigns.edit,
  );
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<MarketingCampaignDetail>();
  const [error, setError] = useState<Error>();
  const [activeSection, setActiveSection] =
    useState<MarketingCampaignDrawerSectionKey>('overview');
  const [editingCampaignUuid, setEditingCampaignUuid] = useState<string>();
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [createLeadOpen, setCreateLeadOpen] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!open || !campaignUuid) {
      return;
    }
    setLoading(true);
    setError(undefined);
    try {
      const resp = await queryMarketingCampaignDetail(campaignUuid);
      setDetail(resp);
    } catch (e) {
      const err = e instanceof Error ? e : new Error('市场活动详情加载失败');
      setError(err);
      setDetail(undefined);
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [campaignUuid, open]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    if (!open) {
      setDetail(undefined);
      setError(undefined);
      setLoading(false);
      return;
    }
    setActiveSection(initialSection);
  }, [initialSection, open]);

  const displayName =
    normalizeText(detail?.name) || fallbackName || '市场活动详情';
  const summaryItems: SummaryItem[] = [
    { label: '当前状态', value: renderStatus(detail?.status) },
    { label: '活动周期', value: buildPeriodText(detail) },
    { label: '负责人', value: resolveOwnerText(detail) },
    { label: '主来源渠道', value: resolveSourceChannelText(detail) },
    { label: '累计线索数', value: detail?.leadCount ?? 0 },
    { label: '有效线索数', value: detail?.validLeadCount ?? 0 },
    { label: '重复线索数', value: detail?.duplicateLeadCount ?? 0 },
    { label: '转客户数', value: detail?.transformedCustomerCount ?? 0 },
    { label: '转商机数', value: detail?.transformedOpportunityCount ?? 0 },
    { label: '赢单金额', value: formatMoney(detail?.wonOpportunityAmount) },
    { label: '创建时间', value: formatCrmDate(detail?.createdAt) },
    { label: '更新时间', value: formatCrmDate(detail?.updatedAt) },
  ];

  return (
    <BusinessModal
      open={open}
      onCancel={onClose}
      destroyOnHidden={false}
      title={null}
      detail
      aria-label="营销活动详情"
    >
      <div className={styles.shell} data-crm-shell>
        {!campaignUuid ? (
          <div className={styles.loading}>
            <Empty description="缺少 uuid 参数" />
          </div>
        ) : loading && !detail ? (
          <div className={styles.loading}>
            <Skeleton active paragraph={{ rows: 10 }} />
          </div>
        ) : !loading && !detail ? (
          <div className={styles.loading}>
            <Empty description={error ? '未找到详情数据' : '未找到详情数据'} />
          </div>
        ) : detail ? (
          <>
            <DetailSurface className={styles.heroCard}>
              <div className={styles.hero} data-crm-hero>
                <div className={styles.heroMain}>
                  <span className={styles.eyebrow}>市场活动详情</span>
                  <div className={styles.heroTopRow} data-crm-hero-row>
                    <div>
                      <div className={styles.titleRow} data-crm-title-row>
                        <Title
                          level={3}
                          className={styles.title}
                          data-crm-title
                        >
                          {displayName}
                        </Title>
                        {renderStatus(detail.status)}
                      </div>
                      <Space wrap size={[8, 8]} className={styles.heroTags}>
                        <Tag>{detail.type || '未设置活动类型'}</Tag>
                        <Tag>{detail.code || '未设置活动编号'}</Tag>
                      </Space>
                    </div>
                    <Space wrap>
                      <Button
                        type="primary"
                        disabled={!campaignUuid}
                        onClick={() => {
                          if (!campaignUuid) {
                            return;
                          }
                          setEditingCampaignUuid(campaignUuid);
                        }}
                      >
                        编辑
                      </Button>
                    </Space>
                  </div>
                  <div className={styles.metaGrid} data-crm-meta>
                    <MetaItem label="负责人" value={resolveOwnerText(detail)} />
                    <MetaItem
                      label="主来源渠道"
                      value={resolveSourceChannelText(detail)}
                    />
                    <MetaItem
                      label="开始时间"
                      value={formatCrmDate(detail.startTime, 'date')}
                    />
                    <MetaItem
                      label="结束时间"
                      value={formatCrmDate(detail.endTime, 'date')}
                    />
                    <MetaItem
                      label="预算"
                      value={formatMoney(detail.budgetAmount)}
                    />
                    <MetaItem
                      label="目标线索数"
                      value={detail.goalLeadCount ?? '-'}
                    />
                    <MetaItem
                      label="目标商机金额"
                      value={formatMoney(detail.goalOpportunityAmount)}
                    />
                    <MetaItem
                      label="赢单金额"
                      value={formatMoney(detail.wonOpportunityAmount)}
                    />
                  </div>
                </div>
              </div>
            </DetailSurface>

            <div className={styles.workspace} data-crm-workspace>
              <aside className={styles.navPanel} data-crm-nav>
                <DetailSurface
                  className={styles.navCard}
                  data-crm-nav-surface
                  title="工作视角"
                >
                  <DetailNavigation
                    className={styles.navMenu}
                    selectedKeys={[activeSection]}
                    items={SECTION_ITEMS.map((section) => ({
                      key: section.key,
                      label: <span>{section.label}</span>,
                    }))}
                    onClick={({ key }) =>
                      setActiveSection(key as MarketingCampaignDrawerSectionKey)
                    }
                  />
                </DetailSurface>
              </aside>

              <div className={styles.mainPanel} data-crm-main>
                {renderSection(activeSection, detail, {
                  canManage: canManageMembers,
                  onAdd: () => setAddLeadOpen(true),
                  onCreate: () => setCreateLeadOpen(true),
                })}
              </div>

              <div className={styles.summaryPanel} data-crm-summary>
                <div className={styles.summaryFrame}>
                  <div className={styles.summaryHead}>
                    <span className={styles.summaryTitle}>活动摘要</span>
                    <span className={styles.summaryStamp}>
                      {formatCrmDate(detail.updatedAt)}
                    </span>
                  </div>
                  <SummaryList items={summaryItems} />
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className={styles.loading}>
            <Empty description="未找到详情数据" />
          </div>
        )}
      </div>
      <MarketingCampaignEditModal
        open={Boolean(editingCampaignUuid)}
        mode="edit"
        uuid={editingCampaignUuid}
        onCancel={() => setEditingCampaignUuid(undefined)}
        onSuccess={() => {
          setEditingCampaignUuid(undefined);
          void loadDetail();
        }}
      />
      <AddLeadToCampaignModal
        open={addLeadOpen}
        campaignUuid={campaignUuid}
        touchpoints={detail?.touchpoints}
        onCancel={() => setAddLeadOpen(false)}
        onSuccess={() => {
          setAddLeadOpen(false);
          void loadDetail();
        }}
      />
      <LeadEditModal
        open={createLeadOpen}
        onOpenChange={setCreateLeadOpen}
        currentMemberUuid={null}
        onSuccess={async (record) => {
          setCreateLeadOpen(false);
          if (!campaignUuid) {
            return;
          }
          if (record?.uuid) {
            try {
              await addMarketingCampaignMember(campaignUuid, {
                leadUuid: record.uuid,
              });
              message.success('线索已创建并关联到活动');
            } catch (e) {
              message.warning(
                e instanceof Error
                  ? `线索已创建，但关联活动失败：${e.message}`
                  : '线索已创建，但关联活动失败',
              );
            }
          }
          void loadDetail();
        }}
      />
    </BusinessModal>
  );
}
